import { useEffect, useState, useContext, useMemo } from 'react';
import { CommonDataContext } from '../../firebase/CommonDataContext';
import { FirebaseNewContext } from '../../firebase/FirebaseNewContext';
import { UserDataContext } from '../../firebase/UserDataContext';
import * as ROUTES from '../../routes/routes';
import { Typography, List, ListItem, ListItemIcon, ListItemText, Paper, Chip, Box, Button, Autocomplete, TextField } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { avgByCategoryInEvent, avgByDivisionInEvent, avgOverallInEvent, getCategoryTopOverallInEvent, getEventCategoryPlaceInEvent, getFinalScoresByTeam, getOverallTopInEvent, getPlacementOverallInEvent, groupedEventsScores, getPlacementOverAllEventsAndCategories } from '../../components/misc/Utils';
import { GlobalSettingsContext } from '../../firebase/GlobalSettingsContext';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import NoTeamImage from '../../images/general-graphic.jpg';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// View-only action cell for teams
const EventActionCellTeam = (params) => {
    const value = params.value || {};
    const entry = value.entry || {};
    const competition = value.competition;
    // determine whether a score object exists (support multiple shapes)
    const entryHasScore = !!(entry && (entry.scoreUid || entry.uid || entry.finalScore || entry.score?.uid));
    const isEntered = !!value.entered || entryHasScore;
    if (!isEntered) return <span>Not Entered Yet</span>;
    const scoreUid = entry.scoreUid || entry.uid || entry.score?.uid;
    if (!scoreUid) return <span>Not Entered Yet</span>;
    const viewHref = ROUTES.routeReplace(ROUTES.SCORING_VIEW, { scoreUid, competition });
    return (
        <a href={viewHref} target="_blank" rel="noopener noreferrer">view score</a>
    );
};

const eventColumnsForTeams = [
    { field: 'col1', headerName: 'Event Number', width: 150, sortComparator: (v1, v2) => Number(v1) - Number(v2) },
    { field: 'col2', headerName: 'Event Name', width: 400 },
    { field: 'col3', headerName: 'Score', width: 150, getApplyQuickFilterFn: () => null },
    { field: 'col4', headerName: 'Category Avg', width: 125, getApplyQuickFilterFn: () => null },
    { field: 'col5', headerName: 'Overall Avg', width: 125, getApplyQuickFilterFn: () => null },
    { field: 'col6', headerName: 'Category Top', width: 125, getApplyQuickFilterFn: () => null },
    { field: 'col7', headerName: 'Overall Top', width: 125, getApplyQuickFilterFn: () => null },
    { field: 'col8', headerName: 'Category Place', width: 125, getApplyQuickFilterFn: () => null },
    { field: 'col9', headerName: 'Overall Place', width: 125, getApplyQuickFilterFn: () => null },
    { field: 'col10', headerName: 'Status', width: 300, renderCell: EventActionCellTeam }
];

const TeamDashboardNew = () => {
    const { firebase } = useContext(FirebaseNewContext)
    const { globalSettings } = useContext(GlobalSettingsContext)
    const { defaultSelectedCompetition, user } = useContext(UserDataContext)
    const { eventsMap: globalEventsMap, teamsMap: globalTeamsMap } = useContext(CommonDataContext)

    const eventsMap = useMemo(() => {
        return globalEventsMap[defaultSelectedCompetition] || {}
    }, [defaultSelectedCompetition, globalEventsMap])
    const [eventsScores, setEventsScores] = useState({})
    const teamsMap = useMemo(() => {
        return globalTeamsMap[defaultSelectedCompetition] || {}
    }, [defaultSelectedCompetition, globalTeamsMap])
    const categoriesMap = useMemo(() => {
        if (Object.keys(globalSettings).length != 0) {
            return globalSettings['categories'][defaultSelectedCompetition]
        }
        return {}
    }, [globalSettings, defaultSelectedCompetition])
    const divisionsMap = useMemo(() => {
        if (Object.keys(globalSettings).length != 0) {
            return globalSettings['divisions'][defaultSelectedCompetition]
        }
        return {}
    }, [globalSettings, defaultSelectedCompetition])
    const showFinalScoresToTeams = useMemo(() => {
        if (Object.keys(globalSettings).length != 0) {
            return globalSettings['showFinalScoresToTeams'][defaultSelectedCompetition]
        }
        return false
    }, [globalSettings, defaultSelectedCompetition])

    useEffect(() => {
        const fetch = async () => {
            let scoresMapResult = await firebase.getScores(defaultSelectedCompetition)
            scoresMapResult = Object.keys(scoresMapResult || {}).reduce((acc, v) => {
                acc[v] = { ...scoresMapResult[v], scoreUid: v }
                return acc
            }, {})

            // TEAM
            getFinalScoresByTeam(scoresMapResult, teamsMap, eventsMap)
            // EVENT
            const eventScoresMap = groupedEventsScores(scoresMapResult, eventsMap, teamsMap)
            avgByCategoryInEvent(eventScoresMap, categoriesMap)
            avgByDivisionInEvent(eventScoresMap, divisionsMap)
            avgOverallInEvent(eventScoresMap)
            getOverallTopInEvent(eventScoresMap)
            getCategoryTopOverallInEvent(eventScoresMap, categoriesMap)
            getEventCategoryPlaceInEvent(eventScoresMap, categoriesMap)
            getPlacementOverallInEvent(eventScoresMap)

            // TEAM
            getPlacementOverAllEventsAndCategories(eventScoresMap, teamsMap, categoriesMap)

            Object.keys(eventScoresMap).reduce((accEvent, e) => {
                const event = eventScoresMap[e]
                accEvent[e] = event
                accEvent[e]['entered'] = accEvent[e]['entered'] || {}
                accEvent[e]['targetScore'] = accEvent[e]['targetScore'] || {}
                const scores = event.scores || []

                (user?.['teamsNew']?.[defaultSelectedCompetition] || []).forEach((teamUid) => {
                    const item = scores.find(x => x.teamUid == teamUid)
                    if (item) {
                        accEvent[e]['entered'][teamUid] = true
                        accEvent[e]['targetScore'][teamUid] = item
                    }
                })

                return accEvent
            }, {})
            Object.keys(eventsMap).reduce((acc, e) => {
                if (!(e in eventScoresMap)) {
                    eventScoresMap[e] = { event: eventsMap[e], entered: {}, targetScore: {} }
                }
                return acc
            }, {})
            setEventsScores(eventScoresMap)
        }
        fetch().catch(console.error)

    }, [firebase, defaultSelectedCompetition, teamsMap, eventsMap, categoriesMap, divisionsMap, user])

    // Dynamically choose columns based on showFinalScoresToTeams
    const columnsToUse = showFinalScoresToTeams ? eventColumnsForTeams : eventColumnsForTeams.filter(c => ['col1','col2','col3','col10'].includes(c.field));

    const userTeamsForCompetition = useMemo(() => {
        return user?.['teamsNew']?.[defaultSelectedCompetition] || [];
    }, [defaultSelectedCompetition, user]);
    const [selectedTeamUid, setSelectedTeamUid] = useState(userTeamsForCompetition[0] || null);

    useEffect(() => {
        if (userTeamsForCompetition.length === 0) {
            setSelectedTeamUid(null);
            return;
        }

        if (!selectedTeamUid || !userTeamsForCompetition.includes(selectedTeamUid)) {
            setSelectedTeamUid(userTeamsForCompetition[0]);
        }
    }, [selectedTeamUid, userTeamsForCompetition]);

    const teamOptions = useMemo(() => {
        return userTeamsForCompetition.map((teamUid) => {
            const team = teamsMap[teamUid] || {};
            const teamNumber = team?.teamNumber || '';
            const teamName = team?.teamName || `Team ${teamUid}`;

            return {
                uid: teamUid,
                label: `${teamNumber ? `#${teamNumber} - ` : ''}${teamName}`,
                searchValue: `${teamNumber} ${teamName}`.toLowerCase()
            };
        });
    }, [teamsMap, userTeamsForCompetition]);

    const selectedTeamOption = useMemo(() => {
        return teamOptions.find((teamOption) => teamOption.uid === selectedTeamUid) || null;
    }, [selectedTeamUid, teamOptions]);

    if (userTeamsForCompetition.length === 0) {
        const competitionName = globalSettings?.competitions?.[defaultSelectedCompetition] || defaultSelectedCompetition || '';
        return (
            <Grid2 container direction="column" alignItems="center" justifyContent="center" sx={{ p: { xs: 2, sm: 3 }, minHeight: '64vh', width: '100%' }}>

                <Box sx={{ width: 720, maxWidth: '92%', textAlign: 'center' }}>
                    <Box sx={{ width: 240, maxWidth: '60%', mx: 'auto', mb: 2 }}>
                        <img src={NoTeamImage} alt="No team" style={{ width: '100%', borderRadius: 8 }} />
                    </Box>

                    <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>Hmm — we couldn't find your team</Typography>
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>Let's run through a few quick checks to get you back on track.</Typography>

                    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <FlagIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={
                                    <Box sx={{ display: 'flex', alignItems: 'left', gap: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{competitionName}</Typography>
                                        <Chip label="Selected" color="primary" size="small" />
                                    </Box>
                                } secondary={"Make sure this is the competition you meant to use."} />
                            </ListItem>

                            <ListItem>
                                <ListItemIcon>
                                    <RadioButtonUncheckedIcon sx={{ color: 'warning.main' }} />
                                </ListItemIcon>
                                <ListItemText primary={<Typography sx={{ fontWeight: 700 }}>Invitation</Typography>} secondary={'Ask your team captain to invite you to this competition.'} />
                            </ListItem>

                            <ListItem>
                                <ListItemIcon>
                                    <RadioButtonUncheckedIcon sx={{ color: 'warning.main' }} />
                                </ListItemIcon>
                                <ListItemText primary={<Typography sx={{ fontWeight: 700 }}>Accept the Invite</Typography>} secondary={'Check your email or account page and accept the team invitation.'} />
                            </ListItem>

                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                                </ListItemIcon>
                                <ListItemText primary={<Typography sx={{ fontWeight: 700 }}>Still Stuck?</Typography>} secondary={'If you completed the steps above but still don\'t see your team, contact an event organizer.'} />
                            </ListItem>

                        </List>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
                            <Button href={ROUTES.ACCOUNT} variant="contained">Switch Competition</Button>
                        </Box>
                    </Paper>
                </Box>
            </Grid2>
        )
    }

    // Build rows for the single events table
    const eventUids = Object.keys(eventsMap || {});
    const primaryTeamUid = selectedTeamUid || userTeamsForCompetition[0];

    const rows = eventUids.map((eKey) => {
        const event = eventsMap[eKey] || {};

        let entry = (eventsScores[eKey]?.targetScore || {})[primaryTeamUid] || {};
        let entered = !!eventsScores[eKey]?.entered?.[primaryTeamUid];
        if ((!entry || Object.keys(entry).length === 0) && eventsScores[eKey]?.scores) {
            const scoresArr = eventsScores[eKey].scores || [];
            const found = scoresArr.find(s => s.teamUid === primaryTeamUid || s.team?.uid === primaryTeamUid || s.team?.teamUid === primaryTeamUid);
            if (found) { entry = found; entered = true; }
        }

        const team = teamsMap[primaryTeamUid] || {};
        const categoryKey = entry?.team?.category || team?.category;

        return {
            id: eKey,
            col1: event?.number || '',
            col2: `${event?.name || ''}${event?.company ? ' - ' + event.company : ''}`,
            col3: entry?.finalScore ?? '-',
            col4: (eventsScores[eKey] && eventsScores[eKey].categoriesAvg && categoryKey) ? (eventsScores[eKey].categoriesAvg[categoryKey] ?? '-') : '-',
            col5: eventsScores[eKey]?.avgOverall ?? '-',
            col6: (eventsScores[eKey] && eventsScores[eKey].categoryTop && categoryKey) ? (eventsScores[eKey].categoryTop[categoryKey] ?? '-') : '-',
            col7: eventsScores[eKey]?.overallTopInEvent ?? '-',
            col8: entry?.categoryPlace ?? '-',
            col9: entry?.overallPlace ?? '-',
            col10: { entry, entered, competition: defaultSelectedCompetition, event: { ...event, eventUid: eKey }, team: { ...team, teamUid: primaryTeamUid } }
        };
    });

    return (
        <Grid2 container direction="column" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, width: '100%' }}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }} >Team Dashboard</Typography>

            {teamOptions.length > 1 && (
                <Box sx={{ mb: 2, maxWidth: 560 }}>
                    <Autocomplete
                        options={teamOptions}
                        value={selectedTeamOption}
                        onChange={(event, newValue) => setSelectedTeamUid(newValue?.uid || null)}
                        isOptionEqualToValue={(option, value) => option.uid === value.uid}
                        getOptionLabel={(option) => option.label}
                        filterOptions={(options, state) => {
                            const value = (state.inputValue || '').trim().toLowerCase();
                            if (!value) {
                                return options;
                            }

                            return options.filter((option) => option.searchValue.includes(value));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search your teams"
                                placeholder="Type team number or team name"
                            />
                        )}
                    />
                </Box>
            )}

                {primaryTeamUid && teamsMap[primaryTeamUid] && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" fontWeight={700}>
                        {teamsMap[primaryTeamUid]?.teamNumber ? `#${teamsMap[primaryTeamUid].teamNumber} - ` : ''}
                        {teamsMap[primaryTeamUid]?.teamName || ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        {teamsMap[primaryTeamUid]?.place != null && (
                            <Chip label={`Overall Place: ${teamsMap[primaryTeamUid].place}`} color="primary" size="medium" />
                        )}
                        {teamsMap[primaryTeamUid]?.categoryPlace != null && (() => {
                            const catName = categoriesMap?.[teamsMap[primaryTeamUid]?.category];
                            return <Chip label={`${catName ? catName + ' ' : 'Category '}Place: ${teamsMap[primaryTeamUid].categoryPlace}`} color="info" size="medium" />;
                        })()}
                        {teamsMap[primaryTeamUid]?.finalScore != null && (
                            <Chip label={`Total Score: ${teamsMap[primaryTeamUid].finalScore}`} color="secondary" size="medium" />
                        )}
                    </Box>
                </Box>
            )}

        <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <Box sx={{ height: 500, minWidth: 700 }}>
                    <DataGrid
                        rows={rows}
                        columns={columnsToUse}
                        initialState={{ sorting: { sortModel: [{ field: 'col1', sort: 'asc' }] } }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
                    />
                </Box>
            </Box>
        </Grid2>
    )
}


export default TeamDashboardNew
